<!--
// Copyright © 2020 Anticrm Platform Contributors.
//
// Licensed under the Eclipse Public License, Version 2.0 (the "License");
// you may not use this file except in compliance with the License. You may
// obtain a copy of the License at https://www.eclipse.org/legal/epl-2.0
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
//
// See the License for the specific language governing permissions and
// limitations under the License.
-->
<script lang="ts">
  import type { IntlString } from '@hcengineering/platform'
  import Label from './Label.svelte'

  export let label: IntlString | undefined = undefined
  export let width: string | undefined = undefined
  export let value: string | undefined = undefined
  export let error: string | undefined = undefined
  export let password: boolean | undefined = undefined
  export let id: string | undefined = undefined
  export let name: string | undefined = undefined
  export let disabled: boolean = false
</script>

<div class="editbox{error ? ' error' : ''}" style={width ? 'width: ' + width : ''}>
  {#if password}
    <input
      type="password"
      class:nolabel={!label}
      {id}
      {name}
      bind:value
      on:blur
      on:change
      on:keyup
      on:input
      autocomplete="off"
      placeholder=" "
      spellcheck="false"
      autocapitalize="off"
      {disabled}
    />
  {:else}
    <input
      type="text"
      class:nolabel={!label}
      {id}
      {name}
      bind:value
      on:blur
      on:change
      on:keyup
      on:input
      autocomplete="off"
      placeholder=" "
      spellcheck="false"
      autocapitalize="off"
      {disabled}
    />
  {/if}
  {#if label}
    <div class="label"><Label {label} /></div>
  {/if}
</div>

<style lang="scss">
  /* Firuza floating-label field (DESIGN.md §6.3): 48 px, radius 6, 12 inside.
     Empty, the label sits where the text goes; focused or filled, it moves up
     to caption size. The focus ring does not move the layout. */
  .editbox {
    position: relative;
    display: flex;
    flex-direction: column;
    padding: 0;
    min-width: 3rem;
    height: 3rem;
    background-color: var(--fz-bg-raised);
    border: 1px solid var(--fz-border-default);
    border-radius: var(--fz-radius-md);
    caret-color: var(--fz-accent-text);
    transition: border-color var(--fz-motion-fast) var(--fz-ease);

    &:hover {
      border-color: var(--fz-border-strong);
    }
    &:focus-within {
      border-color: var(--fz-border-strong);
      outline: 2px solid var(--fz-focus);
      outline-offset: 2px;
    }

    input {
      width: 100%;
      min-width: 0;
      height: 100%;
      margin: 0;
      padding: 1.125rem var(--fz-space-3) 0;
      font-size: var(--fz-type-body-size);
      color: var(--fz-text-primary);
      background-color: transparent;
      border: none;
      border-radius: var(--fz-radius-md);
      outline: none;

      &:disabled {
        color: var(--fz-text-disabled);
        background-color: var(--fz-bg-hover);
      }
    }

    .nolabel {
      padding-top: 0;
    }

    .label {
      position: absolute;
      top: 50%;
      left: var(--fz-space-3);
      font-size: var(--fz-type-body-size);
      line-height: var(--fz-type-body-line);
      color: var(--fz-text-tertiary);
      transform: translateY(-50%);
      transition:
        top var(--fz-motion-base) var(--fz-ease),
        font-size var(--fz-motion-base) var(--fz-ease),
        transform var(--fz-motion-base) var(--fz-ease);
      pointer-events: none;
      user-select: none;
    }
    input:focus + .label,
    input:not(:placeholder-shown) + .label {
      top: 0.375rem;
      font-size: var(--fz-type-caption-size);
      line-height: var(--fz-type-caption-line);
      font-weight: 500;
      color: var(--fz-text-secondary);
      transform: none;
    }
  }
  .error {
    border-color: var(--fz-danger-fg);
    &:focus-within {
      border-color: var(--fz-danger-fg);
      outline-color: var(--fz-danger-fg);
    }
  }
</style>
